import { Shines2Icon, EllipsesIcon } from "@/assets/icons";
import { IconButton, Stack } from "@altimateai/lego";
import { AggregateByValues } from "./constants";
import { ReactNode, useEffect, useState } from "react";
import { api } from "@/helpers/api_helper";
import { z } from "zod";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { TaskLabels, TeamMatesConfig } from "@altimateai/ui-components/teammate";
import { useSelectedInstance } from "../SummaryNew";
import { ChatTriggerLink } from "@altimateai/ui-components/chatbotV2";

import { useGlobalPlaygroundActions } from "@/modules/playground";
import { SummaryTracking } from "@/lib/tracking/summary";

interface Props {
  chartType: "current" | "future";
  analysisType: "cost" | "savings";
  costType?: string;
  savingsType?: string;
  startDate: Date;
  endDate: Date | null;
  aggregation: (typeof AggregateByValues)[keyof typeof AggregateByValues];
}

const SummaryChatButton = ({
  chartType,
  analysisType,
  costType,
  savingsType,
  startDate,
  endDate,
  aggregation,
}: Props) => {
  const [helloMessage, setHelloMessage] = useState<ReactNode | undefined>();
  const chartAnalyserBot = TeamMatesConfig.find(t => t.key === TaskLabels.ChartBot);
  const { openPlayground } = useGlobalPlaygroundActions();

  const instance = useSelectedInstance();

  useEffect(() => {
    const params: Record<string, string | undefined> = {
      start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
      end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
      cost_type: costType,
      savings_type: savingsType,
      chart_type: chartType,
      analysis_type: analysisType,
      cost_analysis_type: analysisType === "cost" ? costType : undefined,
      savings_analysis_type: analysisType === "savings" ? savingsType : undefined,
      aggregation,
    };

    let finalParams = {
      ...params,
    };
    if (instance && instance !== "all") {
      finalParams = {
        ...finalParams,
        instance_id: instance,
      };
    }
    const initialQuestionsSchema = z.object({ questions: z.array(z.string()) });
    api
      .get<{ questions: string[] }>(
        "/summary/v2/datapilot/initial-questions-chart-chat",
        finalParams,
        0,
        initialQuestionsSchema
      )
      .then(response => {
        if (!response) {
          return;
        }
        setHelloMessage(
          <div>
            <p className="al-bg-bg-brand al-p-4 al-mb-4">
              Hi, I am your {chartAnalyserBot?.name}. I can help you understand the {chartType}{" "}
              {analysisType} chart better. Here are few questions that you may be interested in. If
              not, please use the input below to ask any question about this chart.
            </p>
            <Stack direction="column">
              {response.questions.map(question => (
                <ChatTriggerLink key={question} text={question} />
              ))}
            </Stack>
          </div>
        );
      })
      .catch(err => console.error(err));
  }, []);

  const openChatbot = () => {
    SummaryTracking.chartChatbotOpened({
      chart_type: chartType,
      cost_type: costType ?? analysisType,
    });
    openPlayground({
      chatbotProps: { initialMessage: "", helloMessage },
      context: {
        chartType,
        analysisType,
        costType,
        savingsType,
        startDate,
        endDate,
        aggregation,
      },
    });
  };
  return (
    <IconButton
      variant="ghost"
      onClick={openChatbot}
      title="Ask questions about this chart (Beta)"
      className="al-relative"
    >
      <Shines2Icon className="!al-w-[32px] !al-h-[32px]" />
      <EllipsesIcon className="al-absolute al-top-0 al-right-0 al-w-3 al-h-3" />
    </IconButton>
  );
};

export default SummaryChatButton;
